/**
 * RoleGuardedRoute Component
 * Blocks access to routes below the user's role level.
 * Redirects to dashboard if user doesn't meet the minimum level.
 *
 * Mounted under BOTH /internal/implementation/:clientId/* and /embed/* (same
 * component, no duplication) — a denied user inside the embed shell must
 * land back on /embed, never /internal/dashboard (PLAN_embed-account-scope.md,
 * task 8: that redirect ejects them from the partner's iframe into the
 * non-white-label layout). `location.pathname` is checked directly rather
 * than useEmbed(), matching InternalLayout.jsx's own check — this component
 * renders inside both trees, and is the more direct signal either way.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { embedPath } from '@/utils/accountRoutes';

export default function RoleGuardedRoute({ children, minLevel = 0 }) {
    const { user, maxRoleLevel, isSuperAdmin, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) return null;

    // SuperAdmins bypass all level checks
    if (isSuperAdmin()) return children;

    // Check if user meets the minimum level
    if (maxRoleLevel < minLevel) {
        console.warn(`[RoleGuard] Access denied: user level ${maxRoleLevel} < required ${minLevel}`);
        const target = location.pathname.startsWith('/embed') ? embedPath('') : '/internal/dashboard';
        return <Navigate to={target} replace />;
    }

    return children;
}
